import React, { Suspense, lazy } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { FallbackView } from '../../_metronic/partials'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import ActivatedPageRoute from './ActivatedPageRoute'
import { PAGE_PERMISSION } from '../modules/roles/constant/role-default'
import { Page } from '../modules/roles/models/role-model'
import ActivePageBasedRedirect from './ActivePageBasedRedirect'

type Props = {
  activePages: Page[]
}

const PrivateRoutes: React.FC<Props> = ({ activePages = [] }) => {
  const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'))
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))

  const ProjectDetailsPage = lazy<React.FC>(() => import('../modules/projectdetails/ProjectDetailsPage'))
  const PortfolioOverviewPage = lazy<React.FC>(() => import('../modules/portfolio/PortfolioOverviewPage'))
  const ProjectSetupPage = lazy<React.FC>(() => import('../modules/projectsetup/ProjectSetupPage'))
  const CashflowPage = lazy<React.FC>(() => import('../modules/cashflow/CashflowPage'))
  const BudgetPage = lazy<React.FC>(() => import('../modules/budget/BudgetPage'))
  const MilestonesPage = lazy<React.FC>(() => import('../modules/milestones/MilestonesPage'))
  const DocumentRegistryPage = lazy<React.FC>(() => import('../modules/documentregistry/DocumentRegistryPage'))
  const ContactsPage = lazy<React.FC>(() => import('../modules/contacts/ContactsPage'))
  const VariationAssessmentsPage = lazy<React.FC>(() => import('../modules/variationassessments/VariationAssessmentsPage'))
  const ProgressClaimPage = lazy<React.FC>(() => import('../modules/progressclaim/ProgressClaimPage'))
  const PcgReportPage = lazy<React.FC>(() => import('../modules/pcgreport/PcgReportPage'))
  const ChartsPage = lazy<React.FC>(() => import('../modules/charts/ChartPage'))
  const UsersPage = lazy<React.FC>(() => import('../modules/users/UserPage'))
  const RolesPage = lazy<React.FC>(() => import('../modules/roles/RolePage'))
  const SettingsPage = lazy<React.FC>(() => import('../modules/settings/SettingsPage'))
  const AuditsPage = lazy<React.FC>(() => import('../modules/audits/AuditPage'))
  const AuditLogsPage = lazy<React.FC>(() => import('../modules/auditevents/AuditEventsPage'))
  const NotificationsPage = lazy<React.FC>(() => import('../modules/notifications/NotificationsPage'))

  return (
    <Suspense fallback={<FallbackView />}>
      <Switch>
        <ActivatedPageRoute
          path='/portfolio-overview'
          exact
          component={PortfolioOverviewPage}
          permission={[`${PAGE_PERMISSION.PortfolioOverview}.View`]}
          activePages={activePages}
        />
        {/*<Route path='/dashboard' component={DashboardWrapper} /> */}
        {/*<Route path='/user' render={() => <UserPage />} />*/}
        {/*<Route path='/role' render={() => <RolePage />} />*/}

        <ActivatedPageRoute
          path="/dashboard"
          exact
          component={DashboardWrapper}
          permission={[PAGE_PERMISSION.Dashboard,
            `${PAGE_PERMISSION.Dashboard}.View`,
            `${PAGE_PERMISSION.Dashboard}.Create`,
            `${PAGE_PERMISSION.Dashboard}.Update`,
            `${PAGE_PERMISSION.Dashboard}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/project-setup"
          exact
          component={ProjectSetupPage}
          permission={[PAGE_PERMISSION.Project_SetupWizard,
            `${PAGE_PERMISSION.Project_SetupWizard}.View`,
            `${PAGE_PERMISSION.Project_SetupWizard}.Create`,
            `${PAGE_PERMISSION.Project_SetupWizard}.Update`,
            `${PAGE_PERMISSION.Project_SetupWizard}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/project-details"
          exact
          component={ProjectDetailsPage}
          permission={[PAGE_PERMISSION.Project_Details,
            `${PAGE_PERMISSION.Project_Details}.View`,
            `${PAGE_PERMISSION.Project_Details}.Create`,
            `${PAGE_PERMISSION.Project_Details}.Update`,
            `${PAGE_PERMISSION.Project_Details}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/cashflow"
          exact
          component={CashflowPage}
          permission={[PAGE_PERMISSION.Cashflow,
            `${PAGE_PERMISSION.Cashflow}.View`,
            `${PAGE_PERMISSION.Cashflow}.Create`,
            `${PAGE_PERMISSION.Cashflow}.Update`,
            `${PAGE_PERMISSION.Cashflow}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/budget"
          exact
          component={BudgetPage}
          permission={[PAGE_PERMISSION.Budget,
            `${PAGE_PERMISSION.Budget}.View`,
            `${PAGE_PERMISSION.Budget}.Create`,
            `${PAGE_PERMISSION.Budget}.Update`,
            `${PAGE_PERMISSION.Budget}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/milestones"
          exact
          component={MilestonesPage}
          permission={[PAGE_PERMISSION.Milestones,
            `${PAGE_PERMISSION.Milestones}.View`,
            `${PAGE_PERMISSION.Milestones}.Create`,
            `${PAGE_PERMISSION.Milestones}.Update`,
            `${PAGE_PERMISSION.Milestones}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/document-registry"
          exact
          component={DocumentRegistryPage}
          permission={[PAGE_PERMISSION.Document_Registry,
            `${PAGE_PERMISSION.Document_Registry}.View`,
            `${PAGE_PERMISSION.Document_Registry}.Create`,
            `${PAGE_PERMISSION.Document_Registry}.Update`,
            `${PAGE_PERMISSION.Document_Registry}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/contacts"
          exact
          component={ContactsPage}
          permission={[PAGE_PERMISSION.Contacts,
            `${PAGE_PERMISSION.Contacts}.View`,
            `${PAGE_PERMISSION.Contacts}.Create`,
            `${PAGE_PERMISSION.Contacts}.Update`,
            `${PAGE_PERMISSION.Contacts}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/variation-assessments"
          component={VariationAssessmentsPage}
          permission={[PAGE_PERMISSION.Reports_VariationAssessments,
            `${PAGE_PERMISSION.Reports_VariationAssessments}.View`,
            `${PAGE_PERMISSION.Reports_VariationAssessments}.Create`,
            `${PAGE_PERMISSION.Reports_VariationAssessments}.Update`,
            `${PAGE_PERMISSION.Reports_VariationAssessments}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/progress-claim"
          component={ProgressClaimPage}
          permission={[PAGE_PERMISSION.Reports_ProgressClaim,
            `${PAGE_PERMISSION.Reports_ProgressClaim}.View`,
            `${PAGE_PERMISSION.Reports_ProgressClaim}.Create`,
            `${PAGE_PERMISSION.Reports_ProgressClaim}.Update`,
            `${PAGE_PERMISSION.Reports_ProgressClaim}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/pcg-report"
          component={PcgReportPage}
          permission={[PAGE_PERMISSION.Reports_PcgReport,
            `${PAGE_PERMISSION.Reports_PcgReport}.View`,
            `${PAGE_PERMISSION.Reports_PcgReport}.Create`,
            `${PAGE_PERMISSION.Reports_PcgReport}.Update`,
            `${PAGE_PERMISSION.Reports_PcgReport}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/charts"
          component={ChartsPage}
          permission={[PAGE_PERMISSION.Charts,
            `${PAGE_PERMISSION.Charts}.View`,
            `${PAGE_PERMISSION.Charts}.Create`,
            `${PAGE_PERMISSION.Charts}.Update`,
            `${PAGE_PERMISSION.Charts}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/user"
          component={UsersPage}
          permission={[PAGE_PERMISSION.Users,
            `${PAGE_PERMISSION.Users}.View`,
            `${PAGE_PERMISSION.Users}.Create`,
            `${PAGE_PERMISSION.Users}.Update`,
            `${PAGE_PERMISSION.Users}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/role"
          component={RolesPage}
          permission={[PAGE_PERMISSION.Roles,
            `${PAGE_PERMISSION.Roles}.View`,
            `${PAGE_PERMISSION.Roles}.Create`,
            `${PAGE_PERMISSION.Roles}.Update`,
            `${PAGE_PERMISSION.Roles}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/settings"
          exact
          component={SettingsPage}
          permission={[PAGE_PERMISSION.Settings,
            `${PAGE_PERMISSION.Settings}.View`,
            `${PAGE_PERMISSION.Settings}.Create`,
            `${PAGE_PERMISSION.Settings}.Update`,
            `${PAGE_PERMISSION.Settings}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/audit"
          component={AuditsPage}
          permission={[PAGE_PERMISSION.Admin_Audit,
            `${PAGE_PERMISSION.Admin_Audit}.View`,
            `${PAGE_PERMISSION.Admin_Audit}.Create`,
            `${PAGE_PERMISSION.Admin_Audit}.Update`,
            `${PAGE_PERMISSION.Admin_Audit}.Delete`]}
          activePages={activePages}
        />

        <ActivatedPageRoute
          path="/audit-logs"
          component={AuditLogsPage}
          permission={[PAGE_PERMISSION.Admin_Audit_Logs,
            `${PAGE_PERMISSION.Admin_Audit_Logs}.View`,
            `${PAGE_PERMISSION.Admin_Audit_Logs}.Create`,
            `${PAGE_PERMISSION.Admin_Audit_Logs}.Update`,
            `${PAGE_PERMISSION.Admin_Audit_Logs}.Delete`]}
          activePages={activePages}
        />

        <Route path='/notifications' exact component = { NotificationsPage } />

        <ActivePageBasedRedirect></ActivePageBasedRedirect>
        <Redirect to="error/404" />
      </Switch>
    </Suspense>
  )
}

export { PrivateRoutes }
